/**
 * lib/analytics.ts
 *
 * Typed wrapper around window.dataLayer for GTM event pushes.
 *
 * Signup funnel event sequence (always OTP path):
 *   1. signup_page_view      — page mounts
 *   2. signup_otp_requested  — API succeeds, OTP modal shown  { email, mobile, ...utmParams }
 *   3a. signup_completed     — OTP verified, account confirmed { email, mobile, ...utmParams }
 *   3b. signup_otp_abandoned — modal closed without verifying  { email, mobile, ...utmParams }
 *
 *   signup_error fires on any failure (API error, OTP invalid, unexpected throw)
 *
 * Also covers login/logout and the recon job lifecycle (upload wizard, AI
 * wizard, job status, cancellation, report download) — see the trackXxx()
 * functions below the signup ones.
 *
 * Design notes:
 *  - All functions guard against window being undefined (SSR-safe).
 *  - window.dataLayer is initialised by <GoogleTagManager> (pages/_app.tsx,
 *    @next/third-parties/google) before any page component mounts, so
 *    push() calls here will always find an array — even if the GTM script
 *    hasn't fully loaded yet. GTM replays the queue on load.
 *  - Add new event shapes by extending the AnalyticsEvent union type below.
 *  - Every event is auto-tagged with user_id/email via setAnalyticsUser(),
 *    called from oidc-client-auth/AuthProvider.tsx on login/renew/logout —
 *    individual trackXxx() calls never need to pass identity themselves.
 *  - Product events (login/logout/recon) are sent only for free-tier
 *    customers. CDS customer_type is set via setAnalyticsCustomerType()
 *    from usePermissions once getUser() resolves. Signup funnel events
 *    fire before a session exists, so they are not gated.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SignupPageViewEvent {
  event: "signup_page_view";
}

/** All query parameters captured from the signup page URL. */
export type UtmParams = Record<string, string>;

/**
 * Fired when the signup API call succeeds and the OTP modal is shown.
 * Account is created but not yet verified at this point.
 * UTM params included so campaign attribution is visible at this funnel step.
 */
interface SignupOtpRequestedEvent extends UtmParams {
  event: "signup_otp_requested";
  email: string;
  mobile: string;
}

/**
 * Fired when the user closes the OTP modal without verifying.
 * Paired with signup_otp_requested — the delta between the two is your
 * OTP abandonment rate. Includes full attribution payload.
 */
interface SignupOtpAbandonedEvent extends UtmParams {
  event: "signup_otp_abandoned";
  email: string;
  mobile: string;
}

/**
 * Fired when the user successfully verifies their OTP.
 * Account is now fully confirmed. True conversion event.
 * Includes full attribution payload for GA4 / Google Ads forwarding.
 */
interface SignupCompletedEvent extends UtmParams {
  event: "signup_completed";
  email: string;
  mobile: string;
}

interface SignupErrorEvent {
  event: "signup_error";
  error_code?: string;
  error_message?: string;
}

interface LoginSuccessEvent {
  event: "login_success";
}

interface LogoutEvent {
  event: "logout";
}

interface ReconUploadWizardStartedEvent {
  event: "recon_upload_wizard_started";
  recon_type?: string;
}

interface ReconUploadWizardStepEvent {
  event: "recon_upload_wizard_step";
  step: number;
}

interface ReconJobCreatedEvent {
  event: "recon_job_created";
  recon_type: string;
  entity_name?: string;
}

interface ReconAiWizardPhaseEvent {
  event: "recon_ai_wizard_phase";
  /** UI phase, or "answered" when the same agent continues after a reply. */
  phase: string;
  job_id?: string;
}

interface ReconJobStatusEvent {
  event: "recon_job_status";
  job_id: string;
  status: "completed" | "failed" | "cancelled";
}

interface ReconJobCancelRequestedEvent {
  event: "recon_job_cancel_requested";
  job_id: string;
}

interface ReconReportDownloadedEvent {
  event: "recon_report_downloaded";
  job_id: string;
}

// Extend this union as new events are added.
type AnalyticsEvent =
  | SignupPageViewEvent
  | SignupOtpRequestedEvent
  | SignupOtpAbandonedEvent
  | SignupCompletedEvent
  | SignupErrorEvent
  | LoginSuccessEvent
  | LogoutEvent
  | ReconUploadWizardStartedEvent
  | ReconUploadWizardStepEvent
  | ReconJobCreatedEvent
  | ReconAiWizardPhaseEvent
  | ReconJobStatusEvent
  | ReconJobCancelRequestedEvent
  | ReconReportDownloadedEvent;

// ---------------------------------------------------------------------------
// Current user identity — set centrally from oidc-client-auth/AuthProvider's
// updateUser() on every login/renew/logout, so individual trackXxx() calls
// never need to thread identity through non-React code (hooks, services).
// ---------------------------------------------------------------------------

interface AnalyticsUserProfile {
  sub?: string;
  email?: string;
}

let currentAnalyticsUser: { userId?: string; email?: string } | null = null;

export function setAnalyticsUser(profile: AnalyticsUserProfile | null): void {
  currentAnalyticsUser = profile
    ? { userId: profile.sub, email: profile.email }
    : null;
}

// ---------------------------------------------------------------------------
// Customer-type gate — product events only for free-tier customers
// ---------------------------------------------------------------------------

const FREE_CUSTOMER_TYPE = "free";

type SignupEventName =
  | "signup_page_view"
  | "signup_otp_requested"
  | "signup_otp_abandoned"
  | "signup_completed"
  | "signup_error";

function isSignupEvent(event: AnalyticsEvent): event is Extract<
  AnalyticsEvent,
  { event: SignupEventName }
> {
  return event.event.startsWith("signup_");
}

/**
 * `undefined` — CDS type not loaded yet; product events are queued.
 * `null` — logged out; product events are dropped.
 * `"free"` — send product events.
 * any other string (e.g. `"enterprise"`) — drop product events.
 */
let currentCustomerType: string | null | undefined = undefined;
let pendingProductEvents: AnalyticsEvent[] = [];

export function setAnalyticsCustomerType(
  customerType: string | null | undefined
): void {
  currentCustomerType = customerType;

  if (customerType === undefined) {
    return;
  }

  if (customerType !== FREE_CUSTOMER_TYPE) {
    pendingProductEvents = [];
    return;
  }

  const queued = pendingProductEvents;
  pendingProductEvents = [];
  queued.forEach(emitToDataLayer);
}

function shouldSendProductEvent(): boolean | "queue" {
  if (currentCustomerType === undefined) return "queue";
  return currentCustomerType === FREE_CUSTOMER_TYPE;
}

// ---------------------------------------------------------------------------
// Core push helper
// ---------------------------------------------------------------------------

function emitToDataLayer(event: AnalyticsEvent): void {
  const payload = currentAnalyticsUser
    ? {
        ...event,
        ...(currentAnalyticsUser.userId
          ? { user_id: currentAnalyticsUser.userId }
          : {}),
        ...(currentAnalyticsUser.email
          ? { email: currentAnalyticsUser.email }
          : {}),
      }
    : event;

  // Defensive init: <GoogleTagManager> should have already done this, but
  // guard in case this runs before the script tag initializes dataLayer.
  (window as any).dataLayer = (window as any).dataLayer ?? [];
  (window as any).dataLayer.push(payload);

  const isLocalhost =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";

  if (isLocalhost) {
    console.log("[GTM]", payload);
  }
}

function pushToDataLayer(event: AnalyticsEvent): void {
  if (typeof window === "undefined") return;

  // Signup funnel is pre-auth (no customer_type yet) and is the free-tier
  // acquisition path — always send.
  if (isSignupEvent(event)) {
    emitToDataLayer(event);
    return;
  }

  const decision = shouldSendProductEvent();
  if (decision === "queue") {
    pendingProductEvents.push(event);
    return;
  }
  if (!decision) return;

  emitToDataLayer(event);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function trackSignupPageView(queryParams?: UtmParams): void {
  pushToDataLayer({
    ...(queryParams ?? {}),
    event: "signup_page_view",
  });
}

/**
 * Call when the signup API succeeds and the OTP modal is displayed.
 * Account created but not yet verified.
 */
export function trackSignupOtpRequested(
  email: string,
  mobile: string,
  utmParams?: UtmParams
): void {
  pushToDataLayer({
    ...(utmParams ?? {}),
    event: "signup_otp_requested",
    email,
    mobile,
  });
}

/**
 * Call when the OTP modal is closed without the user verifying.
 * Fires from the modal's onClose handler when otpVerified state is false.
 */
export function trackSignupOtpAbandoned(
  email: string,
  mobile: string,
  utmParams?: UtmParams
): void {
  pushToDataLayer({
    ...(utmParams ?? {}),
    event: "signup_otp_abandoned",
    email,
    mobile,
  });
}

/**
 * Call when the user successfully verifies their OTP.
 * This is the true "signup complete" moment — account fully confirmed.
 */
export function trackSignupCompleted(
  email: string,
  mobile: string,
  utmParams?: UtmParams
): void {
  pushToDataLayer({
    ...(utmParams ?? {}),
    event: "signup_completed",
    email,
    mobile,
  });
}

export function trackSignupError(
  errorCode?: string,
  errorMessage?: string,
  queryParams?: UtmParams
): void {
  pushToDataLayer({
    ...(queryParams ?? {}),
    event: "signup_error",
    ...(errorCode ? { error_code: errorCode } : {}),
    ...(errorMessage ? { error_message: errorMessage } : {}),
  });
}

// ---------------------------------------------------------------------------
// Product usage events — recon lifecycle + auth
// ---------------------------------------------------------------------------

export function trackLoginSuccess(): void {
  pushToDataLayer({ event: "login_success" });
}

export function trackLogout(): void {
  pushToDataLayer({ event: "logout" });
}

export function trackReconUploadWizardStarted(reconType?: string): void {
  pushToDataLayer({
    event: "recon_upload_wizard_started",
    ...(reconType ? { recon_type: reconType } : {}),
  });
}

export function trackReconUploadWizardStep(step: number): void {
  pushToDataLayer({ event: "recon_upload_wizard_step", step });
}

export function trackReconJobCreated(
  reconType: string,
  entityName?: string
): void {
  pushToDataLayer({
    event: "recon_job_created",
    recon_type: reconType,
    ...(entityName ? { entity_name: entityName } : {}),
  });
}

export function trackReconAiWizardPhase(phase: string, jobId?: string): void {
  pushToDataLayer({
    event: "recon_ai_wizard_phase",
    phase,
    ...(jobId ? { job_id: jobId } : {}),
  });
}

export function trackReconJobStatus(
  jobId: string,
  status: "completed" | "failed" | "cancelled"
): void {
  pushToDataLayer({ event: "recon_job_status", job_id: jobId, status });
}

export function trackReconJobCancelRequested(jobId: string): void {
  pushToDataLayer({ event: "recon_job_cancel_requested", job_id: jobId });
}

export function trackReconReportDownloaded(jobId: string): void {
  pushToDataLayer({ event: "recon_report_downloaded", job_id: jobId });
}
